import React, { useContext, useEffect, useState } from 'react'
import './Pricing.scss'
import { Context } from '../../contexts/GlobalContext'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/header/header'
import dataFooter from '../../assets/fake-data/data-footer'
import Bottom from '../../components/bottom'
import Footer from '../../components/footer/footer-v1'
import Axios from '../../utilities/axios'

/**
 * @param {Object} props
 * @param {'upgrade' | 'pricing'} [props.type='upgrade']
 * @param {'dark' | 'light' | null} [props.theme=null]
 * @returns {JSX.Element}
 */
const PlanSelector = ({type='upgrade', theme=null}) => {
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [loading, setLoading] = useState(false)
  const { globalState } = useContext(Context)
  const navigate = useNavigate()

  const isSignedIn = !!globalState?.user?._id
  const userTier = globalState?.user?.subscription
  const isProUser = userTier === 'pro'
  const isPricingMode = type === 'pricing'
  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: `Best for anyone who wants to get a glimpse of Nelima's capabilities`,
      pricing: [
        { type: 'Free for Everyone', price: '$0', color: null, icon: 'fal fa-seedling' }
      ],
      features: [
        {
          "details": "Limited Usage",
          "mark": "check"
        },
        {
          "details": "Standard Scale Mode",
          "mark": "check"
        },
        {
          "details": "File Processing (Limited Readability)",
          "mark": "check"
        },
        {
          "details": "Live Internet Access (Limited Browsing)",
          "mark": "check"
        },
        {
          "details": "5 Scheduled Automations",
          "mark": "check"
        },
        {
          "details": "5 GB Agentic Storage",
          "mark": "check"
        },
        {
          "details": "1 Concurrent Agent",
          "mark": "check"
        },
        {
          "details": "Multi-Agent Task Sharing",
          "mark": "cross"
        },
        {
          "details": "Entity Intelligence",
          "mark": "cross"
        },
      ]

    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Best for those who want to 10X their productivity',
      pricing: [
        { type: 'Billed Per Person', price: '$40', icon: 'fal fa-calendar-alt' }
      ],
      features: [
        {
          "details": "High Usage Limit",
          "mark": "check"
        },
        {
          "details": "Extended Long-Running Scale Mode",
          "mark": "check"
        },
        {
          "details": "File Processing (Large Document Support)",
          "mark": "check"
        },
        {
          "details": "Live Internet Access (Unlimited Browsing)",
          "mark": "check"
        },
        {
          "details": "20 Scheduled Automations",
          "mark": "check"
        },
        {
          "details": "20 GB Agentic Storage",
          "mark": "check"
        },
        {
          "details": "20 Concurrent Agents",
          "mark": "check"
        },
        {
          "details": "Multi-Agent Task Sharing",
          "mark": "check"
        },
        {
          "details": "Entity Intelligence",
          "mark": "check"
        },
      ]
    },
    {
      id: 'custom',
      name: 'Custom',
      description: `For Knowledge workers and Teams that want to go to the next level`,
      pricing: [
        { type: 'Custom Pricing', price: null, icon: 'fal fa-tasks' }
      ],
      features: [
        {
          "details": "Fully Customized Usage",
          "mark": "check"
        },
        {
          "details": "All Pro Features",
          "mark": "check"
        }
      ]
    }
  ]

  useEffect(() => {
    // If in upgrade mode and user is not signed in, redirect to home
    if (type === 'upgrade' && !isSignedIn) {
      navigate('/')
    }
  }, [type, isSignedIn, navigate])

  const handlePlanSelect = (planId) => {
    // Disable selection in pricing mode
    if (isPricingMode) return
    setSelectedPlan(planId)
  }

  const handleContinue = async () => {
    // Pricing mode: just navigate to /nelima
    if (isPricingMode) {
      navigate('/nelima')
      return
    }

    // Upgrade mode logic
    if (selectedPlan === 'free') {
      navigate('/nelima')
    } else if (selectedPlan === 'pro') {
      // If already pro, do nothing (button shows "Currently Active")
      if (isProUser) return

      // Make API call to get Stripe checkout session URL
      try {
        setLoading(true)
        const response = await Axios.get('/plan/upgrade-pro')
        const { url } = response.data
        if (url) {
          window.location.href = url
        }
      } catch (error) {
        console.error('Failed to get checkout session:', error)
      } finally {
        setLoading(false)
      }
    } else if (selectedPlan === 'custom') {
      window.location.href = 'https://calendly.com/james-kachamila/30min'
    }
  }

  // Determine button message based on mode and selection
  let buttonMessage = 'No Selection'

  if (isPricingMode) {
    buttonMessage = 'Try Nelima'
  } else {
    // Upgrade mode
    if (selectedPlan === 'free') {
      buttonMessage = 'Try Nelima for free'
    } else if (selectedPlan === 'pro') {
      if (isProUser) {
        buttonMessage = 'Currently Active'
      } else {
        buttonMessage = loading ? 'Loading...' : 'Get Pro Plan'
      }
    } else if (selectedPlan === 'custom') {
      buttonMessage = 'Contact Sales for Custom Plan'
    }
  }

  // Build class names based on mode
  const planSelectorClasses = [
    'plan-selector',
    isPricingMode && 'plan-selector--no-radio',
    isPricingMode && 'plan-selector--disabled-selection'
  ].filter(Boolean).join(' ')

  return (
    <div className={planSelectorClasses} data-theme={theme ? theme : isPricingMode ? 'light' : undefined}>
      {
        type &&
        <div className='plan-selector__back' onClick={() => {
          navigate(-1)
        }}>
          <i className="fal fa-arrow-left"></i>
        </div>
      }
      <main className="plan-selector__main">
        {
          isPricingMode ?
            <h1 className="plan-selector__title-price">Pricing</h1>
            :
            <h1 className="plan-selector__title">Built to grow with you</h1>
        }

        <div className="plan-selector__cards">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`plan-card ${selectedPlan === plan.id ? 'plan-card--selected' : ''}`}
              onClick={() => handlePlanSelect(plan.id)}
            >
              <div className="plan-card__top">
                <div className="plan-card__header">
                  <div className="plan-card__info">
                    <h2 className="plan-card__name">{plan.name}</h2>
                    <p className="plan-card__description">{plan.description}</p>
                  </div>
                  <div className={`plan-card__radio ${selectedPlan === plan.id ? 'plan-card__radio--selected' : ''}`}>
                    {selectedPlan === plan.id && <span className="plan-card__radio-dot"></span>}
                  </div>
                </div>
                <div className="plan-card__pricing">
                  {plan.pricing.map((item, index) => (
                    <div key={index} className="plan-card__price-row">
                      <div className="plan-card__price-type">
                        {item.icon && (
                          <span
                            className="plan-card__price-bullet"
                            style={ item.color ? { color: item.color } : {} }
                          >
                            <i className={item.icon}></i>
                          </span>
                        )}
                        <span>{item.type}</span>
                      </div>
                      {item.price && (
                        <div className="plan-card__price-value">
                          {item.price}<span className="plan-card__price-period">/mo</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {
                isPricingMode && 
                <div className="plan-card__try"
                  onClick={() => {
                    navigate('/nelima', {state: {mayTriggerSignUp: true} })
                  }}
                >Try Nelima</div>
              }
              <div className="plan-card__divider"></div>

              {plan.prefixText && (
                <p className="plan-card__prefix-text">{plan.prefixText}</p>
              )}

              <ul className="plan-card__features">
                {plan.features.map((feature, index) => (
                  <li key={index} className="plan-card__feature">
                    {
                      feature?.mark ? 
                        feature?.mark === 'check' ? 
                          <span className="plan-card__check"><i className="far fa-check"></i></span>
                          :
                          <span className="plan-card__check"><i className="far fa-times"></i></span>
                        :
                        <span className="plan-card__check"><span className="plan-card__check">•</span></span>
                    }
                    <span>{feature.details}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className={`plan-selector__disclaimer ${selectedPlan ? 'plan-selector__disclaimer--hidden' : ''}`}>
          Additional <a href="/usage-limit">usage limits</a> apply. Prices shown don't include applicable tax.
        </p>
      </main>
      {!isPricingMode && selectedPlan && <div className="plan-selector__footer-spacer"></div>}
      {
        !isPricingMode && selectedPlan &&
        <footer className="plan-selector__footer">
          <div className="plan-selector__footer-content">
            <div className="plan-selector__footer-left">
              <div className="plan-selector__footer-info">
                <span className="plan-selector__footer-label">Selected</span>
                <span className="plan-selector__footer-plan-name">
                  {plans.find(p => p.id === selectedPlan)?.name} Plan
                </span>
              </div>
              <div className="plan-selector__footer-info">
                <span className="plan-selector__footer-label">Price</span>
                <div className="plan-selector__footer-price">
                  {selectedPlan === 'free' && <span className="plan-selector__footer-amount">$0<span className="plan-selector__footer-period">/mo</span></span>}
                  {selectedPlan === 'pro' && <span className="plan-selector__footer-amount">$40<span className="plan-selector__footer-period">/mo</span></span>}
                  {selectedPlan === 'custom' && <span className="plan-selector__footer-amount">Custom</span>}
                </div>
              </div>
              <div className="plan-selector__footer-disclaimer">
                Additional <a href="/usage-limit">usage limits</a> apply. Prices shown don't include applicable tax.
              </div>
            </div>
            <div
              className="plan-selector__continue"
              onClick={handleContinue}
            >
              {buttonMessage}
            </div>
          </div>
        </footer>
      }
    </div>
  )
}

/**
 * @param {Object} props
 * @param {'page' | 'component'} [props.type='page']
 * @param {'dark' | 'light' | null} [props.theme=null]
 * @returns {JSX.Element}
 */
const Pricing = ({type='page', theme = null}) => {
  const renderPage = () => {
    return (
      <div className='wrapper-style' id='wrapper'>
        <Header/>
        <PlanSelector type='pricing' theme={theme}/>
        <Footer data={dataFooter}/>
        <Bottom classection='widget-social-footer'/>
      </div>
    )
  }

  const renderComponent = () => {
    return (
      <div className='pricing-style-component'>
        <PlanSelector type='pricing' theme={theme}/>
      </div>
    )
  }

  return type === 'page' ? renderPage() : renderComponent()
}

const Upgrade = () => {
  return <PlanSelector type='upgrade' theme={'dark'}/>
}

export {Pricing, Upgrade}